import PusherServer from "pusher";
import PusherClient from "pusher-js";

// Server-side Pusher instance
export const pusherServer = new PusherServer({
	appId: process.env.PUSHER_APP_ID!,
	key: process.env.NEXT_PUBLIC_PUSHER_KEY!,
	secret: process.env.PUSHER_SECRET!,
	cluster: process.env.NEXT_PUBLIC_PUSHER_CLUSTER!,
	useTLS: true,
});

// Client-side Pusher instance with optimized configuration
export const pusherClient = new PusherClient(
	process.env.NEXT_PUBLIC_PUSHER_KEY!,
	{
		cluster: process.env.NEXT_PUBLIC_PUSHER_CLUSTER!,
		forceTLS: true,
		activityTimeout: 30000, // 30 seconds
		pongTimeout: 10000, // 10 seconds
		authEndpoint: "/api/pusher/auth", // Add authentication endpoint
	}
);

// Event channels
export const CHAT_EVENTS = {
	MESSAGE_SENT: "MESSAGE_SENT",
	USER_TYPING: "USER_TYPING",
	SESSION_UPDATED: "SESSION_UPDATED",
	USER_JOINED: "USER_JOINED",
	USER_LEFT: "USER_LEFT",
	ERROR: "ERROR",
} as const;

// Chat channel name formatter
export function getChatChannelName(sessionId: string) {
	return `presence-chat-${sessionId}`;
}

// Check if a user is online in a specific chat
export async function isUserOnlineInChat(userId: string): Promise<boolean> {
	try {
		const channelName = `user-${userId}`;
		const response = await pusherServer.get({
			path: `/channels/${channelName}`,
		});

		if (response.status === 200) {
			const data = await response.json();
			// Check if user is subscribed to the chat channel
			return data.subscription_count > 0;
		}
		return false;
	} catch (error) {
		console.error("Error checking user online status:", error);
		return false;
	}
}

// Trigger a chat event with error handling
export async function triggerChatEvent(
	sessionId: string,
	event: keyof typeof CHAT_EVENTS,
	data: Record<string, unknown>
) {
	const channelName = getChatChannelName(sessionId);
	try {
		console.log(`Triggering event ${event} on channel ${channelName}`, data);
		return await pusherServer.trigger(channelName, event, data);
	} catch (error) {
		console.error(`Failed to trigger Pusher event ${event}:`, error);
		throw error;
	}
}

// Helper function to check if Pusher is connected
export function isPusherConnected() {
	return pusherClient.connection.state === "connected";
}

// Helper function to reconnect Pusher if disconnected
export async function reconnectPusher() {
	if (!isPusherConnected()) {
		try {
			await pusherClient.connect();
		} catch (error) {
			console.error("Failed to reconnect Pusher:", error);
			throw error;
		}
	}
}
